/**
 * Grade Diagnostics Utility
 * 
 * Helper functions to debug grade processing issues.
 * Expose to window for easy console access.
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

export class GradeDiagnostics {
  /**
   * Check what self-eval grades exist for a conversation
   */
  static async checkSelfEvalGrades(conversationId: string) {
    if (!conversationId) {
      console.error('❌ conversationId is required');
      throw new Error('conversationId is required for checkSelfEvalGrades');
    }
    
    console.log(`🔍 Checking self-eval grades for conversation: ${conversationId}`);
    
    try {
      // Check conversation-level selfEvalGrades collection
      const selfEvalRef = collection(db, 'conversations', conversationId, 'selfEvalGrades');
      const selfEvalSnapshot = await getDocs(selfEvalRef);
      
      console.log(`📊 Found ${selfEvalSnapshot.size} documents in selfEvalGrades collection`);
      
      if (selfEvalSnapshot.empty) {
        console.warn('⚠️  No self-eval grades found in conversations/{id}/selfEvalGrades');
        console.log('   This collection should be populated by the backend after self-eval');
      } else {
        selfEvalSnapshot.docs.forEach(doc => {
          console.log(`  - Document ID: ${doc.id}`);
          console.log(`    Data:`, doc.data());
        });
      }
      
      // Check raw grades collection for self-eval grades
      const gradesRef = collection(db, 'grades');
      const selfEvalQuery = query(
        gradesRef,
        where('conversationId', '==', conversationId),
        where('isSelfEval', '==', true)
      );
      
      const rawGradesSnapshot = await getDocs(selfEvalQuery);
      console.log(`📊 Found ${rawGradesSnapshot.size} raw self-eval grades in grades collection`);
      
      if (rawGradesSnapshot.empty) {
        console.warn('⚠️  No raw self-eval grades found with isSelfEval=true');
        console.log('   Backend might not be tagging self-eval grades correctly');
      } else {
        rawGradesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log(`  - ${data.graderModelId} grading ${data.candidateModelId}: ${data.grade}`);
        });
      }
      
      // Check for ANY grades (peer + self-eval)
      const allGradesQuery = query(
        gradesRef,
        where('conversationId', '==', conversationId)
      );
      
      const allGradesSnapshot = await getDocs(allGradesQuery);
      console.log(`📊 Found ${allGradesSnapshot.size} total grades (peer + self-eval)`);
      
      const peerGrades = allGradesSnapshot.docs.filter(doc => !doc.data().isSelfEval);
      const selfEvalGrades = allGradesSnapshot.docs.filter(doc => doc.data().isSelfEval);
      
      console.log(`  - Peer grades: ${peerGrades.length}`);
      console.log(`  - Self-eval grades: ${selfEvalGrades.length}`);
      console.log(`  - Untagged grades: ${allGradesSnapshot.size - peerGrades.length - selfEvalGrades.length}`);
      
      if (allGradesSnapshot.size - peerGrades.length - selfEvalGrades.length > 0) {
        console.warn('⚠️  Some grades are missing isSelfEval flag!');
        console.log('   These grades might be self-eval but not tagged correctly');
      }
      
      return {
        selfEvalAggregated: selfEvalSnapshot.size,
        selfEvalRaw: rawGradesSnapshot.size,
        totalGrades: allGradesSnapshot.size,
        peerGrades: peerGrades.length,
        selfEvalGrades: selfEvalGrades.length
      };
    } catch (error) {
      console.error('❌ Error checking self-eval grades:', error);
      throw error;
    }
  }
  
  /**
   * Check what model grades exist for a conversation
   */
  static async checkModelGrades(conversationId: string) {
    if (!conversationId) {
      console.error('❌ conversationId is required');
      throw new Error('conversationId is required for checkModelGrades');
    }
    
    console.log(`🔍 Checking model grades for conversation: ${conversationId}`);
    
    try {
      const modelGradesRef = collection(db, 'conversations', conversationId, 'modelGrades');
      const snapshot = await getDocs(modelGradesRef);
      
      console.log(`📊 Found ${snapshot.size} documents in modelGrades collection`);
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log(`  - ${data.modelId}: ${data.majorityJudgment?.medianGrade} (judgments=${data.majorityJudgment?.judgmentsNumber})`);
      });
      
      return snapshot.size;
    } catch (error) {
      console.error('❌ Error checking model grades:', error);
      throw error;
    }
  }
  
  /**
   * Full diagnostic report
   */
  static async fullReport(conversationId: string) {
    if (!conversationId) {
      console.error('❌ conversationId is required');
      console.log('   Usage: gradeDiagnostics.fullReport("your-conversation-id")');
      throw new Error('conversationId is required for fullReport');
    }
    
    console.log('🔬 Running full grade diagnostics...\n');
    
    const selfEval = await this.checkSelfEvalGrades(conversationId);
    console.log('\n');
    const modelGrades = await this.checkModelGrades(conversationId);
    
    console.log('\n📋 Summary:');
    console.log(`  - Model grades (peer-review): ${modelGrades}`);
    console.log(`  - Self-eval aggregated: ${selfEval.selfEvalAggregated}`);
    console.log(`  - Self-eval raw: ${selfEval.selfEvalRaw}`);
    console.log(`  - Total raw grades: ${selfEval.totalGrades}`);
    
    if (selfEval.selfEvalAggregated === 0 && selfEval.selfEvalRaw > 0) {
      console.warn('\n⚠️  ISSUE: Raw self-eval grades exist but not aggregated!');
      console.log('   Backend aggregation might have failed');
    }
    
    if (selfEval.selfEvalRaw === 0 && selfEval.totalGrades > 0) {
      console.warn('\n⚠️  ISSUE: Grades exist but none tagged as self-eval!');
      console.log('   Backend might not be setting isSelfEval flag');
    }
    
    return {
      modelGrades,
      ...selfEval
    };
  }
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).gradeDiagnostics = GradeDiagnostics;
  console.log('💡 Grade diagnostics available at window.gradeDiagnostics');
  console.log('   Try: gradeDiagnostics.fullReport("your-conversation-id")');
}

